import React, { useEffect, useMemo, useState } from "react";
import { Copy, Save, WandSparkles } from "lucide-react";
import { toast } from "react-toastify";
import api from "../../../api/api";
import FilaZeroTimeSelect from "../../../components/FilaZeroTimeSelect";
import "./componentes.css";

const DIAS = [
  ["segunda", "Segunda-feira", "SEG"],
  ["terca", "Terça-feira", "TER"],
  ["quarta", "Quarta-feira", "QUA"],
  ["quinta", "Quinta-feira", "QUI"],
  ["sexta", "Sexta-feira", "SEX"],
  ["sabado", "Sábado", "SÁB"],
  ["domingo", "Domingo", "DOM"],
];

const minutos = (hora) => {
  if (!hora) return 0;
  const [h, m] = String(hora).split(":").map(Number);
  return h * 60 + m;
};

/** Normaliza TIME do banco / input para HH:mm. */
const normalizarHora = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const match = raw.match(/^(\d{1,2}):(\d{2})/);
  if (!match) return "";
  const h = Number(match[1]);
  const m = Number(match[2]);
  if (!Number.isFinite(h) || !Number.isFinite(m) || h > 23 || m > 59) return "";
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};

const PADRAO_SISTEMA = {
  aberturaUtil: "09:00",
  fechamentoUtil: "18:00",
  aberturaSabado: "09:00",
  fechamentoSabado: "13:00",
};

const ConfiguracaoHorarios = ({
  config,
  onSalvar,
  salvando = false,
  estabelecimentoId = null,
}) => {
  const [erro, setErro] = useState("");
  const [horarioEstabelecimento, setHorarioEstabelecimento] = useState({
    abertura: "",
    fechamento: "",
  });
  const [dados, setDados] = useState(() =>
    DIAS.reduce((acc, [dia]) => ({
      ...acc,
      [`horario_${dia}_abertura`]: config[`horario_${dia}_abertura`] || "",
      [`horario_${dia}_fechamento`]: config[`horario_${dia}_fechamento`] || "",
      [`horario_${dia}_ativo`]: Boolean(config[`horario_${dia}_ativo`]),
    }), {})
  );

  useEffect(() => {
    if (!estabelecimentoId) {
      setHorarioEstabelecimento({ abertura: "", fechamento: "" });
      return undefined;
    }

    let cancelado = false;
    (async () => {
      try {
        const { data } = await api.get(`/estabelecimentos/${estabelecimentoId}`);
        if (cancelado) return;
        setHorarioEstabelecimento({
          abertura: normalizarHora(data?.hora_abertura),
          fechamento: normalizarHora(data?.hora_fechamento),
        });
      } catch (error) {
        console.warn("[ConfiguracaoHorarios] falha ao carregar horário do estabelecimento:", error);
        if (!cancelado) setHorarioEstabelecimento({ abertura: "", fechamento: "" });
      }
    })();

    return () => {
      cancelado = true;
    };
  }, [estabelecimentoId]);

  const usaHorarioEstabelecimento = useMemo(() => {
    const { abertura, fechamento } = horarioEstabelecimento;
    return Boolean(abertura && fechamento && minutos(abertura) < minutos(fechamento));
  }, [horarioEstabelecimento]);

  const tituloBotaoPadrao = usaHorarioEstabelecimento
    ? `Usa o horário de Estabelecimentos (${horarioEstabelecimento.abertura}–${horarioEstabelecimento.fechamento}). Seg–sáb abertos, domingo fechado.`
    : "Aplica o padrão do sistema (úteis 09:00–18:00, sábado até 13:00, domingo fechado).";

  const atualizar = (event) => {
    const { name, value, type, checked } = event.target;
    setDados((atual) => ({ ...atual, [name]: type === "checkbox" ? checked : value }));
    setErro("");
  };

  const aplicarPadrao = () => {
    const aberturaEstab = horarioEstabelecimento.abertura;
    const fechamentoEstab = horarioEstabelecimento.fechamento;
    const usarEstab =
      aberturaEstab &&
      fechamentoEstab &&
      minutos(aberturaEstab) < minutos(fechamentoEstab);

    setDados((atual) => {
      const novo = { ...atual };
      DIAS.forEach(([dia]) => {
        const util = !["sabado", "domingo"].includes(dia);
        const sabado = dia === "sabado";

        if (usarEstab) {
          novo[`horario_${dia}_ativo`] = util || sabado;
          novo[`horario_${dia}_abertura`] = util || sabado ? aberturaEstab : "";
          novo[`horario_${dia}_fechamento`] = util || sabado ? fechamentoEstab : "";
          return;
        }

        novo[`horario_${dia}_ativo`] = util || sabado;
        novo[`horario_${dia}_abertura`] =
          util || sabado
            ? sabado
              ? PADRAO_SISTEMA.aberturaSabado
              : PADRAO_SISTEMA.aberturaUtil
            : "";
        novo[`horario_${dia}_fechamento`] = util
          ? PADRAO_SISTEMA.fechamentoUtil
          : sabado
            ? PADRAO_SISTEMA.fechamentoSabado
            : "";
      });
      return novo;
    });

    toast.info(
      usarEstab
        ? `Horário padrão aplicado a partir de Estabelecimentos (${aberturaEstab}–${fechamentoEstab}).`
        : "Horário padrão do sistema aplicado.",
      {
        containerId: "fz-configuracoes",
        toastId: "fz-horario-padrao",
        autoClose: 3500,
      }
    );
  };

  const copiarSegunda = (destino) => {
    setDados((atual) => ({
      ...atual,
      [`horario_${destino}_ativo`]: true,
      [`horario_${destino}_abertura`]: atual.horario_segunda_abertura,
      [`horario_${destino}_fechamento`]: atual.horario_segunda_fechamento,
    }));
  };

  const salvar = async () => {
    const invalido = DIAS.find(([dia]) => {
      if (!dados[`horario_${dia}_ativo`]) return false;
      const abertura = dados[`horario_${dia}_abertura`];
      const fechamento = dados[`horario_${dia}_fechamento`];
      return !abertura || !fechamento || minutos(abertura) >= minutos(fechamento);
    });

    if (invalido) {
      setErro(`Revise os horários de ${invalido[1]}. O fechamento deve ser posterior à abertura.`);
      return;
    }
    await onSalvar(dados, {
      mensagemSucesso: "Horários de funcionamento salvos com sucesso.",
    });
  };

  return (
    <section className="secao-config">
      <div className="section-heading">
        <div>
          <span className="section-eyebrow">JORNADA SEMANAL</span>
          <h2>Horários de funcionamento</h2>
          <p>Defina quando o estabelecimento está aberto. A disponibilidade respeitará estes limites.</p>
        </div>
        <div className="fz-tooltip">
          <button
            type="button"
            className="btn-secondary"
            onClick={aplicarPadrao}
            aria-describedby="horario-padrao-tooltip"
          >
            <WandSparkles size={17} /> Aplicar horário padrão
          </button>
          <span id="horario-padrao-tooltip" className="fz-tooltip__bubble" role="tooltip">
            {tituloBotaoPadrao}
          </span>
        </div>
      </div>

      <p className="muted" style={{ marginTop: "-0.35rem", marginBottom: "0.85rem" }}>
        {usaHorarioEstabelecimento
          ? `O botão usa o horário de Estabelecimentos (${horarioEstabelecimento.abertura}–${horarioEstabelecimento.fechamento}).`
          : "Sem horário em Estabelecimentos: o botão aplica o padrão do sistema."}
      </p>

      {erro && <div className="inline-error" role="alert">{erro}</div>}

      <div className="week-preview" aria-label="Resumo dos horários">
        {DIAS.map(([dia, , curto]) => {
          const ativo = dados[`horario_${dia}_ativo`];
          return (
            <div className={`week-preview__day ${ativo ? "is-open" : ""}`} key={dia}>
              <strong>{curto}</strong>
              <span>{ativo ? `${dados[`horario_${dia}_abertura`] || "--:--"}–${dados[`horario_${dia}_fechamento`] || "--:--"}` : "Fechado"}</span>
            </div>
          );
        })}
      </div>

      <div className="business-hours-list">
        {DIAS.map(([dia, label]) => {
          const ativo = dados[`horario_${dia}_ativo`];
          return (
            <article className={`business-day ${ativo ? "is-active" : ""}`} key={dia}>
              <div className="business-day__name">
                <label className="switch-label">
                  <input
                    type="checkbox"
                    name={`horario_${dia}_ativo`}
                    checked={ativo}
                    onChange={atualizar}
                  />
                  <span className="switch-ui" />
                  <strong>{label}</strong>
                </label>
                <span className={`day-state ${ativo ? "is-open" : ""}`}>{ativo ? "Aberto" : "Fechado"}</span>
              </div>

              {ativo && (
                <div className="business-day__times">
                  <label>
                    <span>Abertura</span>
                    <FilaZeroTimeSelect
                      name={`horario_${dia}_abertura`}
                      value={dados[`horario_${dia}_abertura`]}
                      onChange={atualizar}
                      start="05:00"
                      end="23:30"
                      step={30}
                    />
                  </label>
                  <label>
                    <span>Fechamento</span>
                    <FilaZeroTimeSelect
                      name={`horario_${dia}_fechamento`}
                      value={dados[`horario_${dia}_fechamento`]}
                      onChange={atualizar}
                      start="05:30"
                      end="23:30"
                      step={30}
                    />
                  </label>
                  {dia !== "segunda" && (
                    <button type="button" className="copy-hours" onClick={() => copiarSegunda(dia)}>
                      <Copy size={16} /> Copiar segunda
                    </button>
                  )}
                </div>
              )}
            </article>
          );
        })}
      </div>

      <button
        type="button"
        className="btn-salvar"
        onClick={salvar}
        disabled={salvando}
      >
        <Save size={18} />{" "}
        {salvando ? "Salvando..." : "Salvar funcionamento"}
      </button>
    </section>
  );
};

export default ConfiguracaoHorarios;
